import type {
  PriceListBindingResponse,
  PriceListResponse,
  PriceListSummary,
  RetagChecklistLine,
  RetailStoreOption,
  StoreCoverageRow,
} from '../model/priceListTypes';
import type { PriceLineResponse } from '../model/types';

/**
 * Client-side composition for the price-lists surfaces (design spec §3 A4/Q7 — v1 has no
 * dedicated coverage/staleness endpoint). Pure functions only — the data-fetching
 * (`usePriceListsOverview` in `hooks/index.ts`) hands these Maps/arrays in, so this module is
 * unit-testable without any TanStack Query/mock-api ceremony.
 */

/** The line's tier for quantity 1 (the "unit" price shown everywhere in this workspace). */
function unitTierAmount(line: PriceLineResponse): number | undefined {
  return (line.tiers.find((t) => t.minQuantity === 1) ?? line.tiers[0])?.amount;
}

/**
 * Distinct `productId`s covered by `lines` — NOT `lines.length`. A product legitimately holds
 * more than one governed line for the same list at once (e.g. a currently-ACTIVE line plus a
 * future-dated scheduled one, design spec §8.12's re-tag lead time), so counting rows overstates
 * how many distinct products a list/store actually prices — a two-line, one-product list would
 * otherwise report 200% coverage of a one-product assortment.
 */
export function distinctProductCount(lines: PriceLineResponse[]): number {
  return new Set(lines.map((line) => line.productId)).size;
}

/**
 * The nearest FUTURE `validFrom` among a list's own ACTIVE (governance-approved) lines — the
 * re-tag lead-time signal (design spec §8.12). `state` is the governance lifecycle state (a
 * scheduled line is already `ACTIVE`, just not yet time-window-active), so this deliberately does
 * NOT reuse `isWindowActive` from `priceGrid.ts` (that helper excludes future-dated lines by
 * design — the opposite of what this needs).
 */
export function nextValidFrom(lines: PriceLineResponse[], now: Date = new Date()): string | null {
  const future = lines
    .filter((line) => line.state === 'ACTIVE' && line.validFrom && new Date(line.validFrom) > now)
    .map((line) => line.validFrom as string)
    .sort();
  return future[0] ?? null;
}

/**
 * `Math.round(covered/total * 100)`, clamped to 100 and `null` when either input is unknown —
 * never a fabricated number, and never a number the accompanying `LinearProgress` (itself clamped
 * with `Math.min(100, …)` in `PriceListsPanel`) visually contradicts.
 */
export function coveragePercent(covered: number | null, total: number | null): number | null {
  if (covered === null || total === null || total <= 0) return null;
  return Math.min(100, Math.round((covered / total) * 100));
}

/** Per-list roll-up for the price-lists Lists table (design spec §8.1) and the list detail summary strip (§8.3). */
export function summarizePriceLists(
  lists: PriceListResponse[],
  bindingsByCode: Map<string, PriceListBindingResponse[]>,
  linesByCode: Map<string, PriceLineResponse[]>,
  linesLoadingCodes: Set<string>,
  assortmentTotal: number | null,
  now: Date = new Date(),
): PriceListSummary[] {
  return lists.map((list) => {
    const bindings = bindingsByCode.get(list.code) ?? [];
    const lines = linesByCode.get(list.code);
    const linesCount = lines ? distinctProductCount(lines) : null;
    return {
      list,
      boundStoresCount: bindings.length,
      linesCount,
      coveragePercent: coveragePercent(linesCount, assortmentTotal),
      nextChangeAt: lines ? nextValidFrom(lines, now) : null,
      linesLoading: linesLoadingCodes.has(list.code),
    };
  });
}

/**
 * Per-store roll-up for the Store-coverage sub-view (design spec §8.4) — every directory
 * store renders a row, bound or not (the channel-stock C1 precedent). Inverts the SAME
 * `bindingsByCode` map already fetched for {@link summarizePriceLists}, so no extra request is
 * needed to answer "which list is THIS store bound to".
 */
export function summarizeStoreCoverage(
  stores: RetailStoreOption[],
  lists: PriceListResponse[],
  bindingsByCode: Map<string, PriceListBindingResponse[]>,
  linesByCode: Map<string, PriceLineResponse[]>,
  assortmentTotal: number | null,
  now: Date = new Date(),
): StoreCoverageRow[] {
  const listByCode = new Map(lists.map((list) => [list.code, list] as const));
  const bindingByStore = new Map<string, PriceListBindingResponse>();
  for (const bindings of bindingsByCode.values()) {
    for (const binding of bindings) {
      bindingByStore.set(binding.storeCode, binding);
    }
  }

  return stores.map((store) => {
    const binding = bindingByStore.get(store.code) ?? null;
    const list = binding ? (listByCode.get(binding.priceListCode) ?? null) : null;
    const lines = binding ? linesByCode.get(binding.priceListCode) : undefined;
    const linesCount = lines ? distinctProductCount(lines) : null;
    return {
      store,
      binding,
      list,
      coveragePercent: coveragePercent(linesCount, assortmentTotal),
      nextChangeAt: lines ? nextValidFrom(lines, now) : null,
    };
  });
}

/**
 * The printable re-tag checklist content (design spec §8.12) — every line of `lines` whose
 * `validFrom` matches the target date, paired with whatever is CURRENTLY active for the same
 * product in the same list (so the checklist can show "98,00 ₽ → 95,06 ₽", not just the new price).
 */
export function retagChecklistLines(
  lines: PriceLineResponse[],
  validFrom: string,
  now: Date = new Date(),
): RetagChecklistLine[] {
  return lines
    .filter((line) => line.validFrom === validFrom)
    .flatMap((line) => {
      const newAmount = unitTierAmount(line);
      if (newAmount === undefined) return [];
      const current = lines.find(
        (candidate) =>
          candidate.productId === line.productId &&
          candidate.id !== line.id &&
          candidate.state === 'ACTIVE' &&
          (!candidate.validFrom || new Date(candidate.validFrom) <= now) &&
          (!candidate.validTo || new Date(candidate.validTo) > now),
      );
      return [
        {
          productId: line.productId,
          oldAmount: current ? unitTierAmount(current) : undefined,
          newAmount,
          validFrom,
        },
      ];
    });
}
